import { getAllowlist, parseString, isSafe } from '@exodus/safe-string';
import assert from 'minimalistic-assert';
import parseStackTraceNatively, { stackFramesToString } from './stack.js';
import { omitUndefined } from './utils.js';
import { toCommonErrorMessage } from './common-errors.js';
export const MAX_LINKED_ERRORS_DEPTH = 5;
function makeReadonlySet(values) {
    return new Set(values);
}
const SAFE_CODES_SET = makeReadonlySet(['EPIPE']);
const SAFE_NAMES_SET = makeReadonlySet([
    'Error',
    'AssertionError',
    'TypeError',
    'RangeError',
    'UnknownError',
    'SafeErrorFailedToParse',
    'TimeoutError',
    'SyntaxError',
    'HttpError',
]);
function isSafeName(value) {
    return SAFE_NAMES_SET.has(value) || isSafe(value);
}
function isExodusErrorCode(code) {
    return /^(EXOD-)?\d{1,4}$/u.test(code);
}
function isSafeCode(value) {
    return SAFE_CODES_SET.has(value) || isExodusErrorCode(value);
}
const staticAllowlist = getAllowlist();
const FACTORY_SYMBOL = Symbol('SafeError');
export class SafeError {
    static from(err, depth = 0) {
        let safeName;
        let safeCode;
        let safeHint;
        let linkedSafeErrors;
        try {
            const { name, message, hint, code, cause } = err;
            safeName = isSafeName(name) ? name : 'UnknownError';
            const safeCodeCandidate = `${code}`;
            safeCode = isSafeCode(safeCodeCandidate) ? safeCodeCandidate : undefined;
            const hintCandidates = [hint, message].filter((str) => typeof str === 'string');
            safeHint =
                // chicken sacrifice to TypeScript, otherwise would be hintCandidates.find((str) => isSafe(str))
                hintCandidates.map((str) => parseString(str)).find(Boolean) ||
                    staticAllowlist.find((safePrefix) => hintCandidates.some((str) => str.startsWith(safePrefix))) ||
                    toCommonErrorMessage(message);
            const safeStack = parseStackTraceNatively(err);
            if (cause instanceof Error && depth === 0) {
                linkedSafeErrors = [];
                let currentCause = cause;
                while (currentCause instanceof Error && ++depth <= MAX_LINKED_ERRORS_DEPTH) {
                    try {
                        linkedSafeErrors.push(SafeError.from(currentCause, depth));
                    }
                    catch {
                        linkedSafeErrors.push(FAILED_TO_PARSE_ERROR);
                    }
                    finally {
                        currentCause = currentCause.cause;
                    }
                }
            }
            return new SafeError({
                name: safeName,
                code: safeCode,
                hint: safeHint,
                stack: safeStack,
                linkedErrors: linkedSafeErrors,
                initSymbol: FACTORY_SYMBOL,
            });
        }
        catch {
            try {
                return safeName
                    ? new SafeError({
                        name: safeName,
                        code: safeCode,
                        hint: safeHint,
                        linkedErrors: linkedSafeErrors,
                        initSymbol: FACTORY_SYMBOL,
                    })
                    : FAILED_TO_PARSE_ERROR;
            }
            catch {
                return FAILED_TO_PARSE_ERROR;
            }
        }
    }
    #name;
    #code;
    #hint;
    #stackFrames;
    #linkedErrors;
    #timestamp;
    get name() {
        return this.#name;
    }
    get code() {
        return this.#code;
    }
    get hint() {
        return this.#hint;
    }
    get stackFrames() {
        return this.#stackFrames?.map((frame) => ({ __proto__: null, ...frame }));
    }
    get stack() {
        const stackTrace = stackFramesToString(this.stackFrames);
        return stackTrace
            ? `${this.name}: ${this.code || this.hint || 'unknownHint'}\n${stackTrace}`
            : undefined;
    }
    get linkedErrors() {
        return this.#linkedErrors;
    }
    get timestamp() {
        return this.#timestamp;
    }
    toJSON() {
        return omitUndefined({
            __proto__: null,
            name: this.name,
            code: this.code,
            hint: this.hint,
            stack: this.stack,
            linkedErrors: this.linkedErrors?.map((linkedError) => linkedError.toJSON()),
            timestamp: this.timestamp,
        });
    }
    constructor({ name, code, hint, stack, linkedErrors, initSymbol, }) {
        assert(initSymbol === FACTORY_SYMBOL, 'SafeError: use SafeError.from()');
        this.#name = name;
        this.#code = code;
        this.#hint = hint;
        this.#stackFrames = stack?.map((frame) => ({ ...frame }));
        this.#linkedErrors = linkedErrors;
        this.#timestamp = Date.now();
    }
}
const FAILED_TO_PARSE_ERROR = new SafeError({
    initSymbol: FACTORY_SYMBOL,
    name: 'SafeErrorFailedToParse',
});
